import { toPng } from 'html-to-image';

/** Wait for every <img> in the node to finish loading so we never capture half a card. */
async function imagesReady(node: HTMLElement): Promise<void> {
  const imgs = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise<void>((res) => {
            img.addEventListener('load', () => res(), { once: true });
            img.addEventListener('error', () => res(), { once: true });
          }),
    ),
  );
}

/** Render a DOM node to a PNG, then save it — share sheet on mobile, download on desktop. */
export async function saveNodeAsImage(node: HTMLElement, filename: string): Promise<void> {
  await imagesReady(node);
  // Capture the node's full size — not the clipped viewport height — and run twice:
  // html-to-image's first pass can come back half-rendered while fonts/images warm.
  const opts = { pixelRatio: 2, cacheBust: true, width: node.scrollWidth, height: node.scrollHeight };
  await toPng(node, opts);
  const dataUrl = await toPng(node, opts);

  // iOS Safari ignores the <a download> attribute, so the image just opens and never
  // reaches Photos. The Web Share API gives the native share sheet with "Save Image".
  let file: File | null = null;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    file = new File([blob], filename, { type: 'image/png' });
  } catch {
    file = null;
  }
  if (file && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'My Glow Up' });
    } catch {
      // user cancelled the share sheet — leave it there, don't also trigger a download
    }
    return;
  }

  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Native share sheet when available (mobile), else copy the link to the clipboard. */
export async function shareOrCopy(url: string, text: string): Promise<'shared' | 'copied'> {
  if (navigator.share) {
    try {
      await navigator.share({ text, url });
      return 'shared';
    } catch {
      // user cancelled or share failed — fall back to copy
    }
  }
  await navigator.clipboard.writeText(url);
  return 'copied';
}

/**
 * Copy text to the clipboard. The Clipboard API only works in a secure context (https or
 * localhost) — on a plain-http LAN/sslip host it's unavailable, so we fall back to the
 * legacy execCommand path. Returns whether the copy succeeded.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Native share sheet when available, else copy. Returns what happened. */
export async function shareText(text: string, title?: string): Promise<'shared' | 'copied' | 'failed'> {
  if (navigator.share) {
    try {
      await navigator.share({ title, text });
      return 'shared';
    } catch {
      // cancelled or unsupported — fall back to copy
    }
  }
  return (await copyText(text)) ? 'copied' : 'failed';
}
